import { Component, Injector, OnInit, ViewChild, Input, TemplateRef } from '@angular/core';
import { ThemesLayoutBaseComponent } from '../themes/themes-layout-base.component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { AnnouncementService } from './announcement.service';
import { GetAnnouncementOutputDto, PaymentGatewayServiceProxy } from '@shared/service-proxies/service-proxies';

@Component({
    selector: 'announcement',
    templateUrl: './announcement.component.html',
    styles: [`
        :host { display: flex; align-items: center; height: 100%; }
        .announcement-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; padding: 8px; }
        @media (max-width: 576px) { .announcement-grid { grid-template-columns: repeat(2, 1fr); } }
        .announcement-card { position: relative; border-radius: 12px; overflow: hidden; cursor: pointer; aspect-ratio: 1; background: #222; box-sizing: border-box; border: 2.5px solid transparent; transition: border-color 0.2s; }
        .announcement-card.unread {
            border-color: #4fc3f7;
            box-shadow: 0 0 10px 2px rgba(79,195,247,0.55), 0 0 0 2px rgba(79,195,247,0.18);
            animation: glowPulse 0.8s ease-in-out infinite;
        }
        @keyframes glowPulse {
            0%, 100% { box-shadow: 0 0 6px 2px rgba(79,195,247,0.4), 0 0 0 2px rgba(79,195,247,0.15); }
            50% { box-shadow: 0 0 22px 7px rgba(79,195,247,1), 0 0 0 2px rgba(79,195,247,0.4); }
        }
        .announcement-card img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .announcement-card-title { position: absolute; bottom: 0; left: 0; right: 0; padding: 8px 10px; background: linear-gradient(transparent, rgba(0,0,0,0.65)); color: #fff; font-size: 0.82rem; font-weight: 500; }
        .new-badge {
            position: absolute;
            top: 8px;
            right: 8px;
            background: #f44336;
            color: #fff;
            font-size: 0.65rem;
            font-weight: 700;
            padding: 2px 7px;
            border-radius: 20px;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
    `]
})
export class AnnouncementComponent extends ThemesLayoutBaseComponent implements OnInit {
    @ViewChild('gridModal', { static: true }) gridModalTemplate: TemplateRef<any>;
    @ViewChild('lightboxModal', { static: true }) lightboxModalTemplate: TemplateRef<any>;

    gridModalRef: BsModalRef;
    lightboxModalRef: BsModalRef;

    @Input() customStyle = 'btn btn-icon btn-clean btn-lg mr-1';

    allAnnouncements: GetAnnouncementOutputDto[] = [];
    activeAnnouncements: GetAnnouncementOutputDto[] = [];
    selectedAnnouncement: GetAnnouncementOutputDto | null = null;
    seenIds: Set<number> = new Set();

    isLoadingMore = false;
    private announcementSkip = 0;
    private announcementTotal = 0;
    private readonly PAGE_SIZE = 12;

    get hasMoreAnnouncements(): boolean {
        return this.announcementSkip < this.announcementTotal;
    }

    private readonly COOKIE_PREFIX = 'App.Announcement.Seen.';
    private readonly COOKIE_GRID = 'App.Announcement.GridSeen';

    constructor(
        injector: Injector,
        private _modalService: BsModalService,
        private _announcementService: AnnouncementService,
        private _paymentGatewayService: PaymentGatewayServiceProxy
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this._modalService.onHidden.subscribe(() => {
            this._announcementService.setOpen(false);
        });

        this._announcementService.show$.subscribe(() => this.showGridModal());
        this.loadAnnouncements();
    }

    private getGridSeenValue(): string {
        const ids = this.activeAnnouncements.map(a => a.id).sort().join(',');
        return `${this.getTodayStr()}|${ids}`;
    }

    private loadAnnouncements(): void {
        this.announcementSkip = 0;
        this.allAnnouncements = [];
        this._paymentGatewayService.getPublicAnnouncements(0, this.PAGE_SIZE).subscribe(
            result => {
                this.announcementTotal = result.totalCount;
                this.announcementSkip = (result.items || []).length;
                this.allAnnouncements = result.items || [];
                this.filterActiveAnnouncements();
                this.loadSeenIds();
                this.checkAndAutoShowGridModal();
            },
            () => {
                // silently fail — announcements are non-critical
            }
        );
    }

    loadMoreAnnouncements(): void {
        if (this.isLoadingMore || !this.hasMoreAnnouncements) return;
        this.isLoadingMore = true;
        this._paymentGatewayService.getPublicAnnouncements(this.announcementSkip, this.PAGE_SIZE).subscribe(
            result => {
                this.announcementTotal = result.totalCount;
                this.announcementSkip += (result.items || []).length;
                this.allAnnouncements = [...this.allAnnouncements, ...(result.items || [])];
                this.filterActiveAnnouncements();
                this.loadSeenIds();
                this.isLoadingMore = false;
            },
            () => {
                this.isLoadingMore = false;
            }
        );
    }

    onGridScroll(event: Event): void {
        if (this.isLoadingMore || !this.hasMoreAnnouncements) return;
        const el = event.target as HTMLElement;
        if (el.scrollHeight - el.scrollTop <= el.clientHeight + 100) {
            this.loadMoreAnnouncements();
        }
    }

    private filterActiveAnnouncements(): void {
        const now = new Date();
        this.activeAnnouncements = this.allAnnouncements.filter(a => {
            if (!a.startDate || !a.endDate) return false;
            return a.startDate.toDate() <= now && a.endDate.toDate() >= now;
        });
    }

    private loadSeenIds(): void {
        this.seenIds = new Set<number>();
        this.activeAnnouncements.forEach(a => {
            if (abp.utils.getCookieValue(this.COOKIE_PREFIX + a.id) === '1') {
                this.seenIds.add(a.id);
            }
        });
    }

    isUnread(announcement: GetAnnouncementOutputDto): boolean {
        return !this.seenIds.has(announcement.id);
    }

    private getLatestEndDate(): Date {
        let latest = new Date();
        this.activeAnnouncements.forEach(a => {
            if (a.endDate) {
                const end = a.endDate.toDate();
                if (end > latest) latest = end;
            }
        });
        return latest;
    }

    private markAnnouncementSeen(id: number): void {
        if (this.seenIds.has(id)) return;
        this.seenIds.add(id);
        const announcement = this.activeAnnouncements.find(a => a.id === id);
        const expireDate = announcement?.endDate
            ? announcement.endDate.toDate()
            : this.getLatestEndDate();
        abp.utils.setCookieValue(this.COOKIE_PREFIX + id, '1', expireDate, '/');
    }

    private checkAndAutoShowGridModal(): void {
        if (this.activeAnnouncements.length === 0) return;

        const lastSeen = abp.utils.getCookieValue(this.COOKIE_GRID);
        const todayStr = this.getTodayStr();

        let shouldShow = false;

        if (!lastSeen) {
            shouldShow = true;
        } else {
            const [savedDate, savedIdsStr] = lastSeen.split('|');
            const savedIds = new Set((savedIdsStr ?? '').split(',').filter(Boolean));
            const currentIds = this.activeAnnouncements.map(a => String(a.id));

            const hasNewId = currentIds.some(id => !savedIds.has(id));
            if (savedDate !== todayStr || hasNewId) {
                shouldShow = true;
            }
        }

        if (shouldShow) {
            setTimeout(() => this.showGridModal(true), 1000);
        }
    }

    showGridModal(markSeen = false): void {
        if (this._announcementService.isOpen) return;
        this._openGridModal(markSeen);
    }

    private _openGridModal(markSeen: boolean): void {
        this.filterActiveAnnouncements();
        this.loadSeenIds();

        if (markSeen) {
            abp.utils.setCookieValue(
                this.COOKIE_GRID,
                this.getGridSeenValue(),
                this.getLatestEndDate(),
                '/'
            );
        }

        this._announcementService.setOpen(true);

        this.gridModalRef = this._modalService.show(this.gridModalTemplate, {
            class: 'modal-lg modal-dialog-centered',
            backdrop: true,
            keyboard: true,
            ignoreBackdropClick: false
        });
    }

    closeGridModal(): void {
        if (this.gridModalRef) {
            this.gridModalRef.hide();
            this.gridModalRef = null;
        }
    }

    openLightbox(announcement: GetAnnouncementOutputDto): void {
        this.selectedAnnouncement = announcement;
        this.markAnnouncementSeen(announcement.id);

        if (this.gridModalRef) {
            this.gridModalRef.hide();
            this.gridModalRef = null;
        }

        setTimeout(() => {
            this._announcementService.setOpen(true);

            this.lightboxModalRef = this._modalService.show(this.lightboxModalTemplate, {
                class: 'modal-lg modal-dialog-centered announcement-modal',
                backdrop: true,
                keyboard: true,
                ignoreBackdropClick: false
            });
        }, 300);
    }

    closeLightboxAndReturnToGrid(): void {
        if (this.lightboxModalRef) {
            this.lightboxModalRef.hide();
            this.lightboxModalRef = null;
        }
        setTimeout(() => this._openGridModal(false), 150);
    }

    private getTodayStr(): string {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
}